import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { X, Film, Play, Pause, MapPin, Clock, FileText, Zap } from "lucide-react";

const SEV_COLOR = {
  critical: "#FF2E63", high: "#FF6B35", medium: "#FFC542", low: "#00F0FF",
};

/**
 * AttackStoryPanel — slide-in narrator card that plays through an adversary's
 * full timeline. Given an event (typically a REAL IOC clicked on the globe),
 * fetches the campaign story and animates through it in ~30 seconds.
 *
 * Props:
 *  - eventId: string | null (null → panel closed)
 *  - onClose: () => void
 *  - onCameraFocus: ({lat, lon, altitude}) => void  // optional: parent uses this to spin globe
 */
export default function AttackStoryPanel({ eventId, onClose, onCameraFocus }) {
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [playing, setPlaying] = useState(true);

  useEffect(() => {
    if (!eventId) { setStory(null); return; }
    let ignore = false;
    setLoading(true);
    setProgress(0);
    (async () => {
      try {
        const r = await api.get(`/threat-map/story/${eventId}`);
        if (ignore) return;
        setStory(r.data);
        // Focus the globe on the story's origin
        const src = r.data.focus?.source;
        if (src && onCameraFocus) onCameraFocus({ lat: src.lat, lon: src.lon, altitude: 1.6 });
      } catch {
        setStory(null);
      } finally { setLoading(false); }
    })();
    return () => { ignore = true; };
  }, [eventId, onCameraFocus]);

  // Progress bar animation over ~30s
  useEffect(() => {
    if (!story || !playing) return;
    const total = 30000;   // ms
    const start = Date.now() - progress * total;
    let raf;
    const tick = () => {
      const now = Date.now();
      const p = Math.min(1, (now - start) / total);
      setProgress(p);
      if (p < 1) raf = requestAnimationFrame(tick);
      else setPlaying(false);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line
  }, [story, playing]);

  if (!eventId) return null;

  const focus = story?.focus;
  const stats = story?.stats || {};
  const inv = story?.investigation;
  const relCount = stats.total_events || 0;
  const currentIdx = Math.floor(progress * Math.max(1, relCount - 1));
  const currentEv = story?.related_events?.[currentIdx];

  return (
    <div
      className="fixed right-0 top-0 bottom-0 z-[110] w-[420px] bg-[#0a0a0a] border-l border-white/10 flex flex-col shadow-2xl"
      data-testid="attack-story-panel"
    >
      <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Film className="w-4 h-4 text-[#B57BFF]" />
          <span className="font-display font-black text-lg tracking-tight">ATTACK STORY</span>
        </div>
        <button onClick={onClose}
                data-testid="attack-story-close"
                className="text-[#8E9196] hover:text-white">
          <X className="w-4 h-4" />
        </button>
      </div>

      {loading && (
        <div className="p-6 text-xs text-[#52525B] font-mono">Compiling campaign timeline…</div>
      )}

      {story && (
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Header block */}
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] mb-1">
              Adversary origin
            </div>
            <div className="font-display font-black text-2xl leading-tight">
              {focus?.source?.city}, {focus?.source?.country}
            </div>
            <div className="text-sm font-mono text-[#00F0FF] mt-0.5">{focus?.source?.ip}</div>
            <div className="text-[10px] font-mono text-[#52525B] mt-1">
              IOC {focus?.ioc_kind || "unknown"} · {focus?.geo_source === "live" ? "live GeoIP" : "hashed"}
            </div>
          </div>

          {/* Playback bar */}
          <div>
            <div className="flex items-center gap-2 mb-1">
              <button onClick={() => setPlaying((p) => !p)}
                      data-testid="attack-story-play"
                      className="p-1 border border-white/10 hover:border-[#B57BFF] rounded-sm">
                {playing ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
              </button>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">
                Timeline · {currentIdx + 1}/{relCount}
              </div>
            </div>
            <div className="relative h-1.5 bg-[#141414] rounded-full overflow-hidden">
              <div className="absolute inset-y-0 left-0 bg-[#B57BFF]" style={{ width: `${progress * 100}%` }} />
            </div>
            {currentEv && (
              <div className="mt-2 flex items-center gap-2 text-xs">
                <span className="text-[9px] font-mono uppercase tracking-widest" style={{ color: SEV_COLOR[currentEv.severity] }}>
                  {currentEv.severity}
                </span>
                <span className="text-white/90 truncate">{currentEv.attack_label}</span>
                <span className="ml-auto text-[9px] font-mono text-[#52525B]">
                  {new Date(currentEv.ts).toLocaleTimeString([], { hour12: false })}
                </span>
              </div>
            )}
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-2 gap-2">
            <div className="border border-white/10 rounded-sm p-3">
              <div className="text-[9px] font-mono uppercase tracking-widest text-[#8E9196]">Total events</div>
              <div className="font-display font-black text-xl mt-0.5">{stats.total_events || 0}</div>
            </div>
            <div className="border border-white/10 rounded-sm p-3">
              <div className="text-[9px] font-mono uppercase tracking-widest text-[#8E9196]">Targets hit</div>
              <div className="font-display font-black text-xl mt-0.5">{(stats.targets || []).length}</div>
            </div>
          </div>

          {/* First/last seen */}
          {stats.first_seen && (
            <div className="border border-white/10 rounded-sm p-3">
              <div className="text-[9px] font-mono uppercase tracking-widest text-[#8E9196] mb-1 flex items-center gap-1">
                <Clock className="w-3 h-3" /> Campaign window
              </div>
              <div className="text-xs font-mono">First seen · {new Date(stats.first_seen).toLocaleString()}</div>
              <div className="text-xs font-mono">Last seen · {new Date(stats.last_seen).toLocaleString()}</div>
            </div>
          )}

          {/* Targets & attacks */}
          {stats.targets?.length > 0 && (
            <div className="border border-white/10 rounded-sm p-3">
              <div className="text-[9px] font-mono uppercase tracking-widest text-[#8E9196] mb-1 flex items-center gap-1">
                <MapPin className="w-3 h-3" /> Target cities
              </div>
              <div className="flex flex-wrap gap-1.5">
                {stats.targets.map((t) => (
                  <span key={t} className="text-[10px] font-mono px-1.5 py-0.5 border border-white/10 rounded-sm text-white/80">{t}</span>
                ))}
              </div>
            </div>
          )}

          {stats.attack_labels?.length > 0 && (
            <div className="border border-white/10 rounded-sm p-3">
              <div className="text-[9px] font-mono uppercase tracking-widest text-[#8E9196] mb-1 flex items-center gap-1">
                <Zap className="w-3 h-3" /> Techniques observed
              </div>
              <div className="flex flex-wrap gap-1.5">
                {stats.attack_labels.map((t) => (
                  <span key={t} className="text-[10px] font-mono px-1.5 py-0.5 border border-white/10 rounded-sm text-white/80">{t}</span>
                ))}
              </div>
            </div>
          )}

          {/* Intel hits from free blocklists */}
          {stats.intel_hits?.length > 0 && (
            <div className="border border-[#FF2E63]/30 rounded-sm p-3 bg-[#FF2E63]/[0.05]">
              <div className="text-[9px] font-mono uppercase tracking-widest text-[#FF2E63] mb-2">
                Blocklist hits
              </div>
              {stats.intel_hits.slice(0, 4).map((h, i) => (
                <div key={i} className="text-[11px] py-1">
                  <div className="font-mono text-[#FF2E63]">{h.source}</div>
                  <div className="text-white/70">{h.detail}</div>
                </div>
              ))}
            </div>
          )}

          {/* Linked investigation */}
          {inv && (
            <div className="border border-[#00F0FF]/30 rounded-sm p-3 bg-[#00F0FF]/[0.05]">
              <div className="text-[9px] font-mono uppercase tracking-widest text-[#00F0FF] mb-2 flex items-center gap-1">
                <FileText className="w-3 h-3" /> Linked investigation
              </div>
              <div className="font-mono text-sm text-[#00F0FF]">{inv.case_number}</div>
              <div className="text-sm text-white/90 mt-0.5">{inv.title}</div>
              {inv.narrative?.executive_summary && (
                <div className="text-xs text-[#8E9196] mt-2 leading-relaxed">
                  {inv.narrative.executive_summary}
                </div>
              )}
              {inv.dossier?.likely_actor && (
                <div className="text-[10px] font-mono text-[#B57BFF] mt-2 uppercase tracking-widest">
                  Likely actor · {inv.dossier.likely_actor}
                </div>
              )}
              {inv.narrative?.immediate_actions?.length > 0 && (
                <div className="mt-2">
                  <div className="text-[9px] font-mono uppercase tracking-widest text-[#00F0FF] mb-1">Top 3 actions</div>
                  {inv.narrative.immediate_actions.slice(0, 3).map((a, i) => (
                    <div key={i} className="text-[11px] text-white/80 py-0.5">
                      <span className="text-[#00F0FF]">›</span> {typeof a === "string" ? a : a?.action || JSON.stringify(a)}
                    </div>
                  ))}
                </div>
              )}
              <a href={`/investigations/${inv.id}`}
                 className="mt-3 inline-block text-[10px] font-mono uppercase tracking-widest text-[#00F0FF] hover:underline"
                 target="_blank" rel="noreferrer">
                Open full case →
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
